import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import Header from "../../components/Header/Header";
import { useLocation, useOutletContext, useParams } from "react-router";
import UserStore from "../../store/UserStore";
import AuthStore from "../../store/AuthStore";

import {
  getPath,
  getSubAssetClasses,
  getSubAssetOffshoreReportName,
  getOffshoreInstrumentSheetName,
  isOffshoreInvestment,
  fileColumns,
  getInstrumentColumn,
  btnCellRenderer,
  cellRenderer,
  getOffshoreTransactionColumns,
  getOffshoreSavedData,
} from "../../utils/functions";
import { observer } from "mobx-react";
import SelectNuvama from "../../components/SelectNuvama/SelectNuvama";
import downArrowBlack from "../../assets/images/svg/downArrowBlack.svg";
import Noportfolio from "../../components/Noportfolio/Noportfolio";
import Skeleton from "react-loading-skeleton";
import SubAssetTilesData from "./SubAssetTilesData";
import SubAssetFilter from "./SubAssetFilter";
import {
  errorText,
  noDataOffshore,
  offshoreSummaryId,
  pendingOffshoreSubAssetApiStatus,
} from "../../utils/constants";
import SubAssetTable from "./SubAssetTable";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import DropDownWithInfo from "../../components/OffshoreAssetSummary/DropDownWithInfo";

const AssetDataTabsData = memo(
  ({
    tabs,
    subTabs,
    selectedAssetClass,
    selectedSubAssetClass,
    handleAssetClassSelection,
    handleSubAssetClassSelection,
    isTabLoading,
  }) => {
    return (
      <div className={`sub_asset_tab_container`}>
        {tabs.length ? (
          <>
            <div className={`tabs home_tabs subasset_tabs`}>
              <ul>
                {tabs.map((tabsData, index) => {
                  return (
                    <>
                      <li
                        onClick={() => handleAssetClassSelection(tabsData)}
                        className={
                          tabsData.assetId == selectedAssetClass
                            ? `is-active `
                            : ""
                        }
                      >
                        <button>{tabsData.assetName}</button>
                      </li>
                    </>
                  );
                })}
              </ul>
            </div>

            <div className="tab_container">
              <div className="tabs_part portfolio_sub_asset_tabs">
                <div className="tabs port_tabs sub_asset_tabs">
                  <ul>
                    {subTabs?.map((tabsData, idx) => (
                      <li
                        className={
                          selectedSubAssetClass == tabsData?.id
                            ? "is-active"
                            : "portfolio_tabs_btn_animation"
                        }
                        onClick={() => handleSubAssetClassSelection(tabsData)}
                      >
                        <button>{tabsData?.subAssetName}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="no_tabs_data"></div>
        )}
      </div>
    );
  }
);

const OffshoreDataSubAssetPage = observer(() => {
  const context = useOutletContext();

  const [tabsData, setTabsData] = useState([]);
  const [subTabsData, setSubTabsData] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);

  const [selectedCurrency, setSelectedCurrency] = useState({ currency: "" });

  const [searchDisplay, setSearchDisplay] = useState("");
  const [search, setSearch] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTransactionLoading, setIsTransactionLoading] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNoOffshoreData, setIsNoOffshoreData] = useState(false);

  const [, startTransition] = useTransition();

  const { assetClassId, vehicleId } = useParams();
  const location = useLocation();
  const { path, currency } = getPath({ location });

  const isAssetClassTabLoading = UserStore.isAssetClassTabLoading;
  const isHydrated = UserStore.isHydrated;
  const selectedInstrument = UserStore.selectedOffshoreInstrument;

  const offshoreTabsData = UserStore.offshoreData?.offshoreTabsData;

  const offshoreSubAssetData = UserStore.offshoreData.structuredData;

  const withNuvama = UserStore.withNuvama;
  const withOthers = UserStore.withOthers;

  const controllerRef = useRef(null);

  // helper: abort current controller if it exists
  const abortPending = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  };

  // helper: create a new controller for the next batch of requests
  const newSignal = () => {
    abortPending(); // abort previous in-flight requests
    controllerRef.current = new AbortController();
    return controllerRef.current.signal;
  };

  const getTransactions = ({ data }) => {
    const _instrument = selectedInstrument?.modifiedVehicleData?.find(
      (item) => {
        return data.measure_instr_id === item.measure_instr_id;
      }
    );

    UserStore.setUserStoreData("offshoreInstrumentTransactions", {
      details: _instrument,
    });

    setOpenModal(true);

    setIsTransactionLoading(true);

    UserStore.getOffshoreTransactions({
      instrumentId: _instrument.instrumentId,
      measureinstrumentid: _instrument.measure_instr_id,
      currency: selectedCurrency.currency,
      signal: "",
      onSuccess: (data) => {
        setTransactionsData(data.transaction);
        setIsTransactionLoading(false);
      },
      onFail: () => {
        setTransactionsData([]);
        setIsTransactionLoading(false);
      },
    });
  };

  const getFirstTimePdfTableData = () => {
    const rowData = selectedInstrument?.modifiedVehicleData;

    if (rowData)
      return [...rowData]
        ?.sort(
          (a, b) => Number(a?.data_Curr_Value) - Number(b?.data_Curr_Value)
        )
        .reverse();
  };

  const handleWealth = () => {
    const signal = newSignal(); // abort previous and create fresh signal

    UserStore.setPortfolioTabsStatusDefault();
    UserStore.setOffShoreAssetDataToDefault();
    setTabsData([]);
    setSubTabsData([]);
    setCurrencyList([]);
    setSelectedCurrency({ currency: "" });
    const isOffshore = isOffshoreInvestment();
    // empty all neccessary Data
    const loader = "isFullPageLoaderOffshoreSubAsset";

    UserStore.setUserStoreData(loader, true);

    UserStore.setUserStoreData("offshoreSubAssetApiFetchStatus", {
      ...pendingOffshoreSubAssetApiStatus,
    });
    UserStore.getReportsPersistData();
    if (isOffshore) {
      UserStore.populateSubAssetPage({
        path,
        currency: "",
        offshoreTabsData: UserStore.offshoreSubAssetPageTabs,
        assetClassId: null,
        vehicleId: null,
        signal,
        onSuccess: ({ data }) => {
          setSelectedCurrency({ currency: data });
        },
      });

      setIsNoOffshoreData(false);
    } else {
      setIsNoOffshoreData(true);
      UserStore.setUserStoreData(loader, false);
    }
  };

  const getOffshorePortfoliioOnTabChange = ({
    data,
    savedData,
    currency = "",
  }) => {
    UserStore.setUserStoreData("selectedOffshoreInstrument", {
      ...data,
      ...savedData,
    });

    const isInstrumentList = savedData?.modifiedVehicleData;
    const isInvestmentReturn = savedData.vehicleData;

    // Check if instrument list and investment return for selected asset class and sub asset class according to selected currency is present or not
    // If any of the above data is not present then call their API and display its data or display the saved data
    if (!isInstrumentList || !isInvestmentReturn) {
      const signal = newSignal(); // abort previous and create fresh signal

      const assetClassLoader = "isAssetClassTabLoading";
      const tableLoader = "isInstrumentsDataLoading";

      let classesStatus = {};

      if (isInstrumentList) {
        classesStatus.tableLoader = null;
        classesStatus.instruments = "success";
      } else {
        classesStatus.tableLoader = tableLoader;
        classesStatus.instruments = "pending";
      }
      if (isInvestmentReturn) {
        classesStatus.assetClassLoader = null;
        classesStatus.assetClass = "success";
      } else {
        classesStatus.assetClassLoader = assetClassLoader;
        classesStatus.assetClass = "pending";
      }

      UserStore.setUserStoreData("offshoreSubAssetApiFetchStatus", {
        currency: "success",
        assetClass: classesStatus.assetClass,
        instruments: classesStatus.instruments,
      });

      UserStore.getOffshoreGridAndTableData({
        assetClassId: data.assetClassId,
        vehicleId: data.vehicleId,
        currency: currency || selectedCurrency.currency,
        subAssetClassName: data.subAssetClassName,
        signal,
        assetClassLoader: classesStatus.assetClassLoader,
        tableLoader: classesStatus.tableLoader,
      });
    } else {
      UserStore.setUserStoreData("offshoreSubAssetApiFetchStatus", {
        currency: "success",
        assetClass: "success",
        instruments: "success",
      });
    }
  };

  const handleSubAssetClassSelection = (assetData) => {
    const data = {
      assetClassId: assetData.assetClassId,
      vehicleId: assetData.id,
      subAssetClassName: assetData.subAssetName,
    };

    const currency = assetData.currency;

    setSelectedCurrency({ currency });

    // Check if saved data for the selected asset class and sub asset class for the selected currency is present or not
    const savedData = getOffshoreSavedData({
      data: offshoreSubAssetData,
      assetClassId: data.assetClassId,
      vehicleId: data.vehicleId,
      currency,
    });

    getOffshorePortfoliioOnTabChange({
      data,
      savedData,
      currency,
    });
  };

  const handleAssetClassSelection = (data) => {
    // If a new asset class is selected , then take its first sub asset class and its currency for showing its data on page
    const subAssetData = data.allSubAssetDetails[0];
    const currency = subAssetData.currency;

    const assetClassId = subAssetData.assetClassId;
    const vehicleId = subAssetData.vehicleId;

    setSelectedCurrency({ currency });

    setSubTabsData(data.allSubAssetDetails);
    setCurrencyList(
      subAssetData.currencyWiseData?.filter((currency) => currency.isHolding) ||
        []
    );

    // offshoreSubAssetData i.e. UserStore.offshoreData.structuredData contains saved data of all the asset classes and its sub asset classes
    // Check if data of selected asset class , sub asset class and currency is already fetched or not
    const savedData = getOffshoreSavedData({
      data: offshoreSubAssetData,
      assetClassId,
      vehicleId,
      currency,
    });

    getOffshorePortfoliioOnTabChange({
      data: {
        assetClassId: assetClassId,
        vehicleId: vehicleId,
        subAssetClassName: subAssetData.subAssetName,
      },
      savedData,
      currency,
    });
  };

  const offshoreDataSheetName = useMemo(() => {
    return `${getOffshoreInstrumentSheetName({
      selectedInstrument,
    })} - ${selectedCurrency?.currency || currency}`;
  }, [selectedInstrument, selectedCurrency, currency]);

  useEffect(() => {
    // Set Asset class and sub asset class tabs and currency drop down for the selected sub asset class
    if (isHydrated && offshoreTabsData?.length) {
      const assetClassId = UserStore.selectedOffshoreInstrument.assetClassId;
      const subAssetClassId = UserStore.selectedOffshoreInstrument.vehicleId;

      // OffshoreTabsData consist all the sub asset classes so set it with tabs data
      setTabsData(offshoreTabsData);

      // Get all the sub asset classes of selected asset class
      const subAssetData = offshoreTabsData.find(
        (assetClassData) => assetClassData.assetId == assetClassId
      )?.allSubAssetDetails;
      setSubTabsData(subAssetData || []);

      // Get selected sub asset class and its currency list
      const curentSubAsset = subAssetData.find(
        (subAssetClass) => subAssetClass.id == subAssetClassId
      );

      setCurrencyList(
        curentSubAsset?.currencyWiseData?.filter(
          (currency) => currency.isHolding
        ) || []
      );
    }
  }, [isHydrated, offshoreTabsData]);

  useEffect(() => {
    if (isHydrated && currency) {
      setSelectedCurrency({ currency });
    }
  }, [isHydrated, currency]);

  useEffect(() => {
    UserStore.setUserStoreData("isFullPageLoaderOffshoreSubAsset", true);
    AuthStore.updateLoadingFullScreen(false);
    setIsLoading(false);

    return () => {
      abortPending();
      UserStore.setUserStoreData("selectedCurrency", { currency });
      UserStore.setUserStoreData("selectedOffshoreInstrument", {
        vehicleId: null,
        assetClassId: null,
      });
      UserStore.addToOffshoreData({ offshoreTabsData: [] });
      context.setLocationState("");
    };
  }, []);

  useEffect(() => {
    // Fetch Data on first time page load

    const abortController = new AbortController();
    const { signal } = abortController;

    const isOffshore = isOffshoreInvestment();
    if (isHydrated) {
      if (isOffshore) {
        UserStore.populateSubAssetPage({
          path,
          currency,
          offshoreTabsData: UserStore.offshoreSubAssetPageTabs,
          assetClassId,
          vehicleId,
          signal,
          onSuccess: ({ data, availableCurrency }) => {
            if (availableCurrency) {
              setSelectedCurrency({ currency: availableCurrency });
            } else if (!currency) {
              setSelectedCurrency({ currency: data });
            }
          },
        });
        setIsNoOffshoreData(false);
      } else {
        setIsNoOffshoreData(true);
        UserStore.setUserStoreData("isFullPageLoaderOffshoreSubAsset", false);
      }
    }

    return () => {
      abortController.abort();
    };
  }, [isHydrated]);

  if (UserStore.isFullPageLoaderOffshoreSubAsset || isLoading) {
    return <LoadingScreen />;
  } else {
    return (
      <>
        <Header type={context.type} />
        <div className="direct_equity_out_container">
          {UserStore.offshoreSubAssetApiFetchStatus.currency !== "failed" ? (
            <>
              <div className="direct_equity_outside">
                {!isNoOffshoreData ? (
                  <AssetDataTabsData
                    tabs={tabsData}
                    subTabs={subTabsData}
                    selectedAssetClass={
                      UserStore.selectedOffshoreInstrument.assetClassId
                    }
                    selectedSubAssetClass={
                      UserStore.selectedOffshoreInstrument.vehicleId
                    }
                    handleAssetClassSelection={handleAssetClassSelection}
                    handleSubAssetClassSelection={handleSubAssetClassSelection}
                  />
                ) : (
                  <div className="empty_tabs_offshore"></div>
                )}

                <div className={"direct_equity_nav_1 sub_asset_portfolio"}>
                  <div className="sub-asset-heading-wrapper">
                    {!isNoOffshoreData ? (
                      <>
                        <h2>
                          {selectedInstrument?.subAssetClassName ||
                            selectedInstrument?.vehicleData?.assetClass?.name ||
                            getSubAssetClasses(vehicleId)}
                        </h2>
                        <div className="tooltip-wrapper">
                          <DropDownWithInfo
                            isMenuOpen={isMenuOpen}
                            setIsMenuOpen={setIsMenuOpen}
                            list={{
                              list: currencyList,
                              keyName: "currency",
                            }}
                            selectedItem={selectedCurrency?.currency}
                            applyFilter={(item) => {
                              setSelectedCurrency(item);

                              const {
                                assetClassId,
                                vehicleId,
                                subAssetClassName,
                              } = UserStore.selectedOffshoreInstrument;

                              const currency = item.currency;

                              // Check saved data for selected asset class , sub asset class and selected currency is present or not
                              const savedData = getOffshoreSavedData({
                                data: UserStore.offshoreData.structuredData,
                                assetClassId,
                                vehicleId,
                                currency,
                              });

                              getOffshorePortfoliioOnTabChange({
                                data: {
                                  assetClassId,
                                  vehicleId,
                                  subAssetClassName,
                                },
                                savedData,
                                currency,
                              });
                            }}
                            popupMenuClass="currency_popup"
                          />
                        </div>
                      </>
                    ) : (
                      <h2>
                        {selectedInstrument?.subAssetClassName ||
                          selectedInstrument?.vehicleData?.assetClass?.name ||
                          getSubAssetClasses(vehicleId)}
                      </h2>
                    )}
                  </div>

                  <SelectNuvama
                    type={"all"}
                    handleWealth={(title) => handleWealth(title)}
                    withNuvama={withNuvama}
                    withOthers={withOthers}
                    selectedMember={"0"}
                    memberDropdownClass="analytics-members-dropdown"
                    memberDropdownTextClass="analytics-members-dropdown-text"
                    downArrowIcon={downArrowBlack}
                    pointerEvent={false}
                    disableToggle={true}
                  />
                </div>
                {UserStore.offshoreSubAssetApiFetchStatus.assetClass ===
                  "failed" &&
                UserStore.offshoreSubAssetApiFetchStatus.instruments ===
                  "falied" ? (
                  <Noportfolio
                    noDataMsg={errorText}
                    isTextOnly={true}
                    msgClass="error_text_msg_offshore"
                  />
                ) : isNoOffshoreData ? (
                  <Noportfolio
                    noDataMsg={noDataOffshore}
                    isTextOnly={false}
                    msgClass=""
                  />
                ) : (
                  <>
                    <div
                      className={`direct_equity_below ${"direct_equity_below_non_infinity"}`}
                    >
                      {
                        <div className="direct_money_value_container">
                          {isAssetClassTabLoading ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <Skeleton
                                count={1}
                                height="25px"
                                className="skel"
                              />
                              <Skeleton count={1} height="25px" />
                            </div>
                          ) : UserStore.offshoreSubAssetApiFetchStatus
                              .assetClass !== "failed" ? (
                            <SubAssetTilesData
                              currentInvestmentReturns={
                                selectedInstrument?.vehicleData
                                  ?.investmentreturn
                              }
                              selectedInstrument={selectedInstrument}
                            />
                          ) : (
                            <div className="error_screen clr_normal">
                              <p>{errorText}</p>
                            </div>
                          )}
                        </div>
                      }
                      <h2 className="portfolio_breadcr_title"></h2>
                    </div>

                    {UserStore.offshoreSubAssetApiFetchStatus.instruments !==
                    "failed" ? (
                      <>
                        {!!selectedInstrument?.modifiedVehicleData && (
                          <SubAssetFilter
                            reportName={getSubAssetOffshoreReportName({
                              getData: UserStore.getData,
                              userData: UserStore.userData,
                            })}
                            printId={offshoreSummaryId}
                            exportDetails={UserStore?.exportDetails}
                            restrictHover={false}
                            search={searchDisplay}
                            sheetName={offshoreDataSheetName}
                            selectedInstrument={selectedInstrument}
                            setSearch={(data) => {
                              setSearchDisplay(data);
                              startTransition(() => {
                                setSearch(data);
                              });
                            }}
                            getFirstTimePdfTableData={getFirstTimePdfTableData}
                            columns={fileColumns}
                            customSheetName={getOffshoreInstrumentSheetName({
                              selectedInstrument,
                            })}
                          />
                        )}
                        <SubAssetTable
                          selectedInstrument={selectedInstrument}
                          columns={getInstrumentColumn({
                            cellRenderer,
                            btnCellRenderer,
                            getTransactions,
                            clientWidth: document.documentElement.clientWidth,
                          })}
                          showColumnsOnly
                          search={search}
                          d={4}
                          pdfData={getFirstTimePdfTableData()}
                          fileColumns={fileColumns}
                          openModal={openModal}
                          setOpenModal={setOpenModal}
                          transactionsData={transactionsData}
                          transactionColumns={getOffshoreTransactionColumns({
                            currency: selectedCurrency?.currency || currency,
                          })}
                          headerName={offshoreDataSheetName}
                          isTransactionLoading={isTransactionLoading}
                        />
                      </>
                    ) : (
                      <div className="table_error_screen clr_normal">
                        <p>{errorText}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="error_page_offshore_subAsset clr_normal">
              <p>{errorText}</p>
            </div>
          )}
        </div>
      </>
    );
  }
});

export default OffshoreDataSubAssetPage;
